import React, { useState } from 'react';
import { X } from 'lucide-react';

const CATEGORIES = ['FAQ', 'Tarifs', 'Services', 'Instructions', 'Localisation', 'Garantie', 'Information', 'Actualités'];

export const KnowledgeModal = ({ item, onClose, onSave }) => {
  const isEditing = !!item;
  const [mode, setMode]           = useState('manual');
  const [title, setTitle]         = useState(isEditing ? (item.title || item.question || '') : '');
  const [content, setContent]     = useState(isEditing ? (item.content || item.answer || '') : '');
  const [category, setCategory]   = useState(isEditing ? (item.category || 'FAQ') : 'FAQ');
  const [urlInput, setUrlInput]   = useState('');
  const [urlCategory, setUrlCategory] = useState('Information');
  const [saving, setSaving]       = useState(false);
  const [importing, setImporting] = useState(false);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) { alert('Titre et contenu requis'); return; }
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      const url = isEditing
        ? `${window.location.origin}/api/chat/knowledge-base/${item.id || item._id}`
        : `${window.location.origin}/api/chat/knowledge-base`;
      const resp = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), content: content.trim(), category })
      });
      if (resp.ok) { onSave(); onClose(); }
      else { const err = await resp.json().catch(() => ({})); alert(`❌ ${err.detail || 'Erreur'}`); }
    } catch { alert('❌ Erreur réseau'); }
    finally { setSaving(false); }
  };

  const handleUrlImport = async (e) => {
    e.preventDefault();
    if (!urlInput.trim()) { alert('URL requise'); return; }
    setImporting(true);
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${window.location.origin}/api/chat/knowledge-base/import-url`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlInput.trim(), category: urlCategory })
      });
      const data = await resp.json();
      if (resp.ok) { alert(`✅ ${data.message}`); onSave(); onClose(); }
      else { alert(`❌ ${data.detail || 'Erreur'}`); }
    } catch { alert('❌ Erreur réseau'); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">
              {isEditing ? '✏️ Modifier la connaissance' : '➕ Ajouter une Connaissance'}
            </h3>
            <button onClick={onClose} className="hover:bg-gray-100 p-2 rounded-full"><X size={24} /></button>
          </div>

          {!isEditing && (
            <div className="flex border-b mb-5">
              <button onClick={() => setMode('manual')}
                className={`px-5 py-2 font-semibold text-sm border-b-2 transition ${mode === 'manual' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}>
                ✍️ Manuel
              </button>
              <button onClick={() => setMode('url')}
                className={`px-5 py-2 font-semibold text-sm border-b-2 transition ${mode === 'url' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500'}`}>
                🔗 Importer depuis une URL
              </button>
            </div>
          )}

          {(mode === 'manual' || isEditing) && (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Catégorie *</label>
                <select value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Titre / Question *</label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} required
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400"
                  placeholder="Ex: Délai de réparation ?" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Contenu / Réponse *</label>
                <textarea value={content} onChange={e => setContent(e.target.value)} required rows={6}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 resize-y"
                  placeholder="Réponse détaillée..." />
                <p className="text-xs text-gray-400 mt-1">{content.length} caractères</p>
              </div>
              <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                ℹ️ Le bot utilisera automatiquement cette connaissance dans ses réponses
              </div>
              <div className="flex space-x-3">
                <button type="submit" disabled={saving}
                  className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg font-semibold">
                  {saving ? 'Sauvegarde...' : isEditing ? '✅ Enregistrer' : '✅ Ajouter'}
                </button>
                <button type="button" onClick={onClose}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold">Annuler</button>
              </div>
            </form>
          )}

          {mode === 'url' && !isEditing && (
            <form onSubmit={handleUrlImport} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">URL à indexer *</label>
                <input type="url" value={urlInput} onChange={e => setUrlInput(e.target.value)} required
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-400 font-mono text-sm"
                  placeholder="https://www.ifixit.com/Guide/..." />
                <p className="text-xs text-gray-500 mt-1">Le contenu de la page sera extrait et indexé automatiquement</p>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Catégorie</label>
                <select value={urlCategory} onChange={e => setUrlCategory(e.target.value)}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-400">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="bg-orange-50 border border-orange-200 p-3 rounded text-sm text-orange-800">
                🔗 Le titre sera extrait automatiquement depuis la page.
              </div>
              <div className="flex space-x-3">
                <button type="submit" disabled={importing}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg font-semibold">
                  {importing ? '⏳ Import en cours...' : '🔗 Importer la page'}
                </button>
                <button type="button" onClick={onClose}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold">Annuler</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
